<script setup>
/**
 * Where you are in the six screens in front of a paid send, and which of them you may
 * jump back to.
 *
 * EVERY REACHABLE STEP IS A REAL LINK, not a click handler. That is the whole difference
 * between this sequence and the wizard it replaces: `to` is `?step=<id>` on the compose
 * route, so a step is a URL — the browser's own Back button walks the sequence backwards,
 * a step can be bookmarked and reopened, and middle-clicking one opens it in a tab. A
 * `@click` that mutated a `currentStep` ref would look identical on screen and none of
 * those three would be true.
 *
 * A STEP YOU HAVE NOT EARNED IS NOT A LINK. Forward steps render as plain text, because
 * the page's own guard (`helpers/broadcast-compose.js` `resolveStep`) would bounce a click
 * on one straight back — and a link that silently lands somewhere else is worse than one
 * that was never offered. The guard is still the authority; this only agrees with it.
 *
 * The rail is a `<nav><ol>`: it is an ordered list of destinations, `aria-current="step"`
 * says which one is open, and a screen reader gets the position without needing the
 * "Step 3 of 6" line the sighted reader gets from the header.
 */
import { computed } from 'vue';

import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /** Step ids in order, from `stepsFor(template)`. */
  steps: { type: Array, required: true },
  current: { type: String, required: true },
  /** Ids the guard would allow — everything complete, plus the current one. */
  reachable: { type: Array, default: () => [] },
  /** `step => routeLocation`. */
  linkFor: { type: Function, required: true },
});

const { t } = useWasloI18n();

const KEY = {
  name: 'NAME',
  channel: 'CHANNEL',
  message: 'MESSAGE',
  fill: 'FILL',
  audience: 'AUDIENCE',
  check: 'CHECK',
};

const rows = computed(() =>
  props.steps.map((step, index) => ({
    step,
    number: index + 1,
    label: t(`WASLO.BROADCASTS.COMPOSE.STEPS.${KEY[step]}`),
    isCurrent: step === props.current,
    // Done means "behind the one you are on", which is the only definition that cannot
    // disagree with the guard: the guard admits a step only when every step before it is
    // complete, so anything before the current step is complete by construction.
    isDone: props.steps.indexOf(step) < props.steps.indexOf(props.current),
    isReachable: props.reachable.includes(step) && step !== props.current,
  }))
);
</script>

<template>
  <nav data-waslo-step-rail>
    <ol class="flex flex-wrap items-center gap-x-2 gap-y-2">
      <li
        v-for="(row, index) in rows"
        :key="row.step"
        class="flex items-center gap-2"
      >
        <component
          :is="row.isReachable ? 'router-link' : 'span'"
          :to="row.isReachable ? linkFor(row.step) : undefined"
          :aria-current="row.isCurrent ? 'step' : undefined"
          class="inline-flex items-center gap-2 h-8 px-2.5 rounded-lg text-sm"
          :class="[
            row.isCurrent
              ? 'bg-n-blue-3 text-n-blue-11 font-medium'
              : 'text-n-slate-11',
            row.isReachable ? 'hover:bg-n-alpha-2 cursor-pointer' : '',
            !row.isCurrent && !row.isDone && !row.isReachable
              ? 'text-n-slate-10'
              : '',
          ]"
          :data-waslo-step="row.step"
          :data-waslo-step-state="
            row.isCurrent ? 'current' : row.isDone ? 'done' : 'ahead'
          "
        >
          <span
            v-if="row.isDone"
            class="size-4 i-lucide-check text-n-blue-10"
            aria-hidden="true"
          />
          <span v-else class="w-4 text-xs tabular-nums" aria-hidden="true">
            {{ row.number }}
          </span>
          {{ row.label }}
        </component>
        <!--
          The separator mirrors on `rtl:`, because a chevron is the one glyph whose meaning
          IS its direction. Never after the last row.
        -->
        <span
          v-if="index < rows.length - 1"
          class="size-3 i-lucide-chevron-right rtl:rotate-180 text-n-slate-9"
          aria-hidden="true"
        />
      </li>
    </ol>
  </nav>
</template>
